/**
 * /financeiro/integracoes-bancarias — Open Banking (Fase 13.4) · redesign FIN-7.
 *
 * Fiel ao mockup Fase 5 V2.2 (#integracoes): tabela de conexões com provedor
 * (pay-pill), switch de sincronização automática, status (badge) e ações
 * (Sincronizar / Histórico / Reconectar / Desativar) + "Conectar conta".
 *
 * Camada visual; toda a lógica (Pluggy Connect widget, mock, sincronizar,
 * histórico) é a mesma. O switch de auto-sync agora persiste via PATCH.
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiAlertTriangle, FiPlus, FiRefreshCw } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import {
  atualizarConexaoBancaria,
  criarConexaoBancaria,
  desativarConexaoBancaria,
  gerarConnectTokenPluggy,
  listarCategorias,
  listarConexoesBancarias,
  listarContas,
  listarProvidersOpenBanking,
  obterHistoricoSincronizacao,
  sincronizarConexao,
} from "../../../services/financeiro";
import { PageContainer } from "../../../styles/shared";

import { EmptyMsg, Input, Select } from "../../Orcamentos/styles";


const STATUS_TONE = {
  EM_ANDAMENTO: "info",
  SUCESSO: "success",
  ERRO_PROVIDER: "danger",
  ERRO_PARSE: "warning",
};

function dataHora(iso) {
  if (!iso) return "Nunca";
  return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}


export default function IntegracoesBancarias() {
  const { request, hasPermission } = useAuth();
  // Mesmo gate do ConexaoBancariaViewSet: sincronizar = importar; criar,
  // reconectar, desativar, parear e ligar o auto-sync = configurar.
  const podeConfigurar = hasPermission("financeiro:configurar");
  const podeImportar = hasPermission("financeiro:importar");

  const [conexoes, setConexoes] = useState([]);
  const [contas, setContas] = useState([]);
  const [providers, setProviders] = useState([]);
  const [categoriasReceita, setCategoriasReceita] = useState([]);
  const [categoriasDespesa, setCategoriasDespesa] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modal nova conexao
  const [modalNova, setModalNova] = useState(false);
  const [novoContaId, setNovoContaId] = useState("");
  // A15: id da conta NO PROVIDER. Quando o banco tem mais de uma conta no
  // mesmo item, o backend devolve a lista e o usuário escolhe aqui.
  const [novoContaExterna, setNovoContaExterna] = useState("");
  const [contasDoProvider, setContasDoProvider] = useState([]);
  // A15: conexao criada ANTES do pareamento ficou com `conta_id_externa`
  // vazio. O erro de sincronizacao manda "abra Integracoes bancarias e
  // escolha qual conta" — e a tela nao tinha esse controle para conexao
  // que ja existe. Banco com corrente + poupanca (o caso comum) ficava
  // travado para sempre.
  const [pareando, setPareando] = useState(null);
  const [contasParaParear, setContasParaParear] = useState([]);
  const [contaExternaEscolhida, setContaExternaEscolhida] = useState("");
  const [novoProvider, setNovoProvider] = useState("mock");
  const [novoUsername, setNovoUsername] = useState("");
  const [novoCatReceita, setNovoCatReceita] = useState("");
  const [novoCatDespesa, setNovoCatDespesa] = useState("");

  // Modal historico
  const [modalHistorico, setModalHistorico] = useState(null);
  const [historico, setHistorico] = useState([]);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [conexs, ctas, prvs, catR, catD] = await Promise.all([
        listarConexoesBancarias(request),
        listarContas(request, { ativo: true }),
        listarProvidersOpenBanking(request),
        listarCategorias(request, { tipo: "RECEITA", ativo: true }),
        listarCategorias(request, { tipo: "DESPESA", ativo: true }),
      ]);
      setConexoes(Array.isArray(conexs) ? conexs : conexs?.results || []);
      setContas(Array.isArray(ctas) ? ctas : ctas?.results || []);
      setProviders(Array.isArray(prvs) ? prvs : []);
      setCategoriasReceita(Array.isArray(catR) ? catR : catR?.results || []);
      setCategoriasDespesa(Array.isArray(catD) ? catD : catD?.results || []);
    } catch (err) {
      setError(err?.message || "Erro ao carregar integrações bancárias.");
    } finally { setLoading(false); }
  }, [request]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirModal = () => {
    setModalNova(true);
    setNovoContaId(""); setNovoProvider("mock"); setNovoUsername("");
    setNovoCatReceita(""); setNovoCatDespesa("");
  };

  const criar = async () => {
    if (!novoContaId) { setError("Selecione uma conta."); return; }
    if (novoProvider === "pluggy") { await criarConexaoPluggy(); return; }
    try {
      await criarConexaoBancaria(request, {
        conta_id: novoContaId,
        conta_id_externa: novoContaExterna || undefined,
        provider: novoProvider,
        credenciais: { username: novoUsername || "teste" },
        categoria_receita_default_id: novoCatReceita || null,
        categoria_despesa_default_id: novoCatDespesa || null,
      });
      setModalNova(false);
      setContasDoProvider([]);
      setNovoContaExterna("");
      carregar();
    } catch (err) {
      // Banco com mais de uma conta: o backend devolve a lista em vez de
      // adivinhar. Mostra as opções em vez de um erro seco.
      const opcoes = err?.payload?.contas_disponiveis;
      if (Array.isArray(opcoes) && opcoes.length) {
        setContasDoProvider(opcoes);
        setError("Este banco tem mais de uma conta — escolha qual vincular.");
        return;
      }
      setError(err?.message || "Erro ao criar conexão.");
    }
  };

  const criarConexaoPluggy = async () => {
    setError(null);
    try {
      const tokenResp = await gerarConnectTokenPluggy(request, {});
      if (!tokenResp?.access_token) {
        setError("Pluggy não retornou Connect Token. Verifique PLUGGY_CLIENT_ID/SECRET no backend.");
        return;
      }
      const { PluggyConnect } = await import("pluggy-connect-sdk");
      const widget = new PluggyConnect({
        connectToken: tokenResp.access_token,
        includeSandbox: false,
        onSuccess: async ({ item } = {}) => {
          if (!item?.id) { setError("Pluggy Connect não retornou itemId."); return; }
          try {
            await criarConexaoBancaria(request, {
              conta_id: novoContaId,
              provider: "pluggy",
              credenciais: { item_id: item.id },
              categoria_receita_default_id: novoCatReceita || null,
              categoria_despesa_default_id: novoCatDespesa || null,
            });
            setModalNova(false);
            carregar();
          } catch (err) {
            setError(err?.message || "Erro ao registrar conexão após Pluggy Connect.");
          }
        },
        onError: (err) => {
          setError((err && (err.message || err.code)) || "Pluggy Connect falhou.");
        },
        onClose: () => {},
      });
      widget.init();
    } catch (err) {
      setError(err?.message || "Falha ao carregar Pluggy Connect SDK. Rode 'npm install' no dash-pcp/.");
    }
  };

  const atualizarConexaoPluggy = async (conexao) => {
    setError(null);
    try {
      const tokenResp = await gerarConnectTokenPluggy(request, { item_id: conexao.item_id });
      if (!tokenResp?.access_token) { setError("Pluggy não retornou Connect Token de update."); return; }
      const { PluggyConnect } = await import("pluggy-connect-sdk");
      const widget = new PluggyConnect({
        connectToken: tokenResp.access_token,
        includeSandbox: false,
        updateItem: conexao.item_id,
        onSuccess: () => { carregar(); },
        onError: (err) => { setError((err && (err.message || err.code)) || "Update falhou."); },
      });
      widget.init();
    } catch (err) {
      setError(err?.message || "Falha ao reabrir Pluggy Connect.");
    }
  };

  const sincronizar = async (conexao) => {
    const desde = window.prompt(
      "Data início (YYYY-MM-DD):",
      conexao.ultima_sincronizacao_em
        ? conexao.ultima_sincronizacao_em.slice(0, 10)
        : new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10),
    );
    if (!desde) return;
    const ate = window.prompt("Data fim (YYYY-MM-DD):", new Date().toISOString().slice(0, 10));
    if (!ate) return;
    try {
      const out = await sincronizarConexao(request, conexao.id, { desde, ate });
      window.alert(
        `Status: ${out.status}\n` +
        `Transações recebidas: ${out.transacoes_recebidas || 0}\n` +
        `Lancamentos criados: ${out.lancamentos_criados || 0}\n` +
        `Ignorados (duplicatas): ${out.lancamentos_ignorados_duplicados || 0}\n` +
        `Erros: ${out.erros || 0}`,
      );
      carregar();
    } catch (err) {
      window.alert(`Erro: ${err?.message || "Falha na sincronização"}`)
    }
  };

  const abrirPareamento = async (conexao) => {
    setPareando(conexao);
    setContaExternaEscolhida(conexao.conta_id_externa || "");
    setContasParaParear([]);
    // Não existe endpoint de "listar contas do provider": quem devolve a
    // lista é o próprio backend, no payload do erro, quando o banco expõe
    // mais de uma conta. É o mesmo mecanismo do fluxo de criação.
    try {
      const hoje = new Date().toISOString().slice(0, 10);
      await sincronizarConexao(request, conexao.id, { desde: hoje, ate: hoje });
    } catch (err) {
      const opcoes = err?.payload?.contas_disponiveis;
      if (Array.isArray(opcoes) && opcoes.length) setContasParaParear(opcoes);
    }
  };

  const salvarPareamento = async () => {
    await atualizarConexaoBancaria(request, pareando.id, {
      conta_id_externa: contaExternaEscolhida,
    });
    setPareando(null);
    await carregar();
  };

  const toggleAuto = async (conexao) => {
    try {
      await atualizarConexaoBancaria(request, conexao.id, {
        sincroniza_automaticamente: !conexao.sincroniza_automaticamente,
      });
      carregar();
    } catch (err) {
      setError(err?.message || "Erro ao alterar sincronização automática.");
    }
  };

  const verHistorico = async (conexao) => {
    setModalHistorico(conexao);
    try {
      const h = await obterHistoricoSincronizacao(request, conexao.id);
      setHistorico(Array.isArray(h) ? h : []);
    } catch (err) {
      setHistorico([]);
    }
  };

  const desativar = async (conexao) => {
    if (!window.confirm(`Desativar conexão da ${conexao.conta_nome}?`)) return;
    try {
      await desativarConexaoBancaria(request, conexao.id);
      carregar();
    } catch (err) {
      window.alert(`Erro: ${err?.message}`);
    }
  };

  return (
    <PageContainer style={{ maxWidth: "none" }} className="fin2">
      <div className="flex between" style={{ alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="breadcrumb">
            <span>Início</span><span className="crumb-sep">/</span>
            <span>Financeiro</span><span className="crumb-sep">/</span>
            <span className="active">Integrações</span>
          </div>
          <h1 className="page-title">Integrações bancárias</h1>
          <div className="page-sub">Open Banking via Pluggy · sincronização automática de extratos</div>
        </div>
        {podeConfigurar && (
          <button type="button" className="btn btn--primary" onClick={abrirModal}>
            <FiPlus size={14} aria-hidden="true" /> Conectar conta
          </button>
        )}
      </div>

      {error && <div className="empty"><div className="msg"><FiAlertTriangle size={14} aria-hidden="true" /> {error}</div></div>}

      <div className="table-wrap">
        <table className="dtable">
          <colgroup>
            <col style={{ width: "24%" }} /><col style={{ width: 110 }} /><col style={{ width: 160 }} />
            <col style={{ width: 96 }} /><col style={{ width: 130 }} /><col />
          </colgroup>
          <thead><tr>
            <th>Conta</th><th>Provedor</th><th>Últ. sincronização</th>
            <th>Sinc. auto.</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {conexoes.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 0 }}><div className="empty"><div className="msg">
                {loading
                  ? "Carregando…"
                  : podeConfigurar
                    ? "Nenhuma conexão bancária. Clique em “Conectar conta”."
                    : "Nenhuma conexão bancária."}
              </div></div></td></tr>
            )}
            {conexoes.map((c) => (
              <tr key={c.id} style={!c.ativo ? { background: "var(--warning-bg)" } : undefined}>
                <td><strong>{c.conta_nome}</strong></td>
                <td><span className="pay-pill">{c.provider_display}</span></td>
                <td className="mono">{dataHora(c.ultima_sincronizacao_em)}</td>
                <td>
                  <button type="button" role="switch" aria-checked={!!c.sincroniza_automaticamente}
                          aria-label={`Auto-sync ${c.conta_nome}`} disabled={!c.ativo || !podeConfigurar}
                          className={"switch" + (c.sincroniza_automaticamente ? " is-on" : "")}
                          onClick={() => c.ativo && podeConfigurar && toggleAuto(c)} />
                </td>
                <td>
                  {c.ativo
                    ? <span className="badge success no-dot">Conectada</span>
                    : <span className="badge warning no-dot">Inativa</span>}
                </td>
                <td>
                  <div className="flex gap-2" style={{ justifyContent: "flex-end" }}>
                    {c.ativo && (
                      <>
                        {podeImportar && (
                          <AsyncButton variant="secondary" silentSuccess silentError
                                       style={{ padding: "5px 10px", fontSize: 12 }}
                                       onClick={() => sincronizar(c)}>
                            <FiRefreshCw size={12} aria-hidden="true" /> Sincronizar
                          </AsyncButton>
                        )}
                        {podeConfigurar && c.provider === "pluggy" && (
                          <AsyncButton variant="secondary" silentSuccess silentError
                                       style={{ padding: "5px 10px", fontSize: 12 }}
                                       title="Re-autenticar (consentimento expirado, MFA)"
                                       onClick={() => atualizarConexaoPluggy(c)}>Reconectar</AsyncButton>
                        )}
                        {podeConfigurar && !c.conta_id_externa && (
                          <button type="button" className="btn btn--ghost btn--sm"
                                  title="Esta conexão não sabe qual conta do banco ler"
                                  style={{ color: "var(--warning)" }}
                                  onClick={() => abrirPareamento(c)}>
                            Escolher conta
                          </button>
                        )}
                        <button type="button" className="btn btn--ghost btn--sm"
                                onClick={() => verHistorico(c)}>Histórico</button>
                        {podeConfigurar && (
                          <AsyncButton variant="danger" silentSuccess silentError
                                       style={{ padding: "5px 10px", fontSize: 12 }}
                                       onClick={() => desativar(c)}>Desativar</AsyncButton>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="muted" style={{ padding: "12px 18px", borderTop: "1px solid var(--border)", fontSize: 12 }}>
          A sincronização roda automaticamente para as conexões com auto-sync ligado e alimenta a fila de Conciliação. Mock gera transações fictícias; Pluggy é Open Finance regulado.
        </div>
      </div>

      {/* Modal Nova Conexao */}
      <SlideOverPanel open={!!pareando} onClose={() => setPareando(null)}
                      title="Escolher a conta do banco" width={520}
                      footer={
                        <>
                          <button type="button" className="btn btn--secondary"
                                  onClick={() => setPareando(null)}>Cancelar</button>
                          <button type="button" className="btn btn--primary"
                                  disabled={!contaExternaEscolhida}
                                  style={{ marginLeft: 8 }}
                                  onClick={salvarPareamento}>Salvar</button>
                        </>
                      }>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <p style={{ margin: 0, fontSize: 13, opacity: 0.85 }}>
            Esta conexão não sabe qual conta do banco ler. Bancos que expõem
            corrente e poupança precisam da escolha — sem ela a sincronização
            fica travada.
          </p>
          {contasParaParear.length > 0 ? (
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>Conta *</span>
              <select className="input" value={contaExternaEscolhida}
                      onChange={(e) => setContaExternaEscolhida(e.target.value)}>
                <option value="">— escolha —</option>
                {contasParaParear.map((c) => (
                  <option key={c.id || c.conta_id_externa} value={c.id || c.conta_id_externa}>
                    {c.nome || c.name || c.id}
                    {c.numero ? ` · ${c.numero}` : ""}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>
                ID da conta no banco *
              </span>
              <input className="input" value={contaExternaEscolhida}
                     onChange={(e) => setContaExternaEscolhida(e.target.value)}
                     placeholder="accountId do provider" />
              <span style={{ fontSize: 11, opacity: 0.7 }}>
                O banco não devolveu a lista agora. O identificador está no
                painel do provider.
              </span>
            </label>
          )}
        </div>
      </SlideOverPanel>

      <SlideOverPanel open={modalNova} onClose={() => setModalNova(false)}
                      title="Nova conexão bancária" width={560}
                      footer={
                        <>
                          <button type="button" className="btn btn--secondary" onClick={() => setModalNova(false)}>Cancelar</button>
                          <button type="button" className="btn btn--primary" onClick={criar} disabled={!novoContaId} style={{ marginLeft: 8 }}>Criar conexão</button>
                        </>
                      }>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {contasDoProvider.length > 0 && (
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>
                Qual conta do banco vincular? *
              </span>
              <select
                className="input"
                value={novoContaExterna}
                onChange={(e) => setNovoContaExterna(e.target.value)}
              >
                <option value="">— escolha —</option>
                {contasDoProvider.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                    {c.numero ? ` · ${c.numero}` : ""}
                    {c.tipo ? ` (${c.tipo})` : ""}
                  </option>
                ))}
              </select>
              <span style={{ fontSize: 11, opacity: 0.7 }}>
                Este banco expôs mais de uma conta. A sincronização só traz
                as transações da conta escolhida.
              </span>
            </label>
          )}
          <label>
            <strong>Conta financeira</strong>
            <Select value={novoContaId} onChange={(e) => setNovoContaId(e.target.value)}>
              <option value="">— Selecione uma conta —</option>
              {contas.map((c) => (
                <option key={c.id} value={c.id}>{c.nome} ({c.tipo_display})</option>
              ))}
            </Select>
          </label>
          <label>
            <strong>Provider</strong>
            <Select value={novoProvider} onChange={(e) => setNovoProvider(e.target.value)}>
              {providers.map((p) => (
                <option key={p.id} value={p.id} disabled={!p.disponivel}>
                  {p.label} {!p.disponivel && "(placeholder)"}
                </option>
              ))}
            </Select>
            {providers.find((p) => p.id === novoProvider) && (
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
                {providers.find((p) => p.id === novoProvider).observacao}
              </div>
            )}
          </label>
          {novoProvider === "mock" && (
            <label>
              <strong>Username (para mock)</strong>
              <Input value={novoUsername} onChange={(e) => setNovoUsername(e.target.value)} placeholder="teste" />
            </label>
          )}
          {novoProvider === "pluggy" && (
            <div style={{ padding: 12, background: "var(--warning-bg)", border: "1px solid var(--warning-border)", borderRadius: 6, fontSize: 13, lineHeight: 1.4 }}>
              <strong>Como vai funcionar:</strong> ao clicar “Criar conexão”, o widget Pluggy Connect abrirá em overlay. Você selecionará o banco, autenticará via OAuth do próprio banco e escolherá quais contas compartilhar. Após sucesso, a conexão será registrada automaticamente.
            </div>
          )}
          <label>
            <strong>Categoria default para receitas</strong>
            <Select value={novoCatReceita} onChange={(e) => setNovoCatReceita(e.target.value)}>
              <option value="">—</option>
              {categoriasReceita.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </Select>
          </label>
          <label>
            <strong>Categoria default para despesas</strong>
            <Select value={novoCatDespesa} onChange={(e) => setNovoCatDespesa(e.target.value)}>
              <option value="">—</option>
              {categoriasDespesa.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </Select>
          </label>
        </div>
      </SlideOverPanel>

      {/* Modal Historico */}
      <SlideOverPanel open={!!modalHistorico}
                      onClose={() => { setModalHistorico(null); setHistorico([]); }}
                      title={`Histórico — ${modalHistorico?.conta_nome || ""}`} width={640}>
        {historico.length === 0 ? (
          <EmptyMsg>Nenhuma sincronização realizada ainda.</EmptyMsg>
        ) : (
          <div className="table-wrap fin2">
            <table className="dtable">
              <thead><tr>
                <th>Data</th><th>Período</th><th>Status</th>
                <th className="num" style={{ textAlign: "right" }}>Recebidas</th>
                <th className="num" style={{ textAlign: "right" }}>Criadas</th>
                <th className="num" style={{ textAlign: "right" }}>Dupl.</th>
              </tr></thead>
              <tbody>
                {historico.map((h) => (
                  <tr key={h.id}>
                    <td className="mono">{dataHora(h.iniciada_em)}</td>
                    <td className="mono">{h.periodo_inicio} – {h.periodo_fim}</td>
                    <td><span className={"badge no-dot " + (STATUS_TONE[h.status] || "neutral")}>{h.status_display}</span></td>
                    <td className="num">{h.transacoes_recebidas}</td>
                    <td className="num">{h.lancamentos_criados}</td>
                    <td className="num">{h.lancamentos_ignorados_duplicados}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SlideOverPanel>
    </PageContainer>
  );
}
